import type { DataFreshness, SourceFreshness } from '../services/tracking.service';

const COST_LAG_TOLERANCE_MS = 2 * 60 * 60_000;

export type FreshnessState = 'ok' | 'late' | 'unknown';

/** ISO from the API carries a zone; ClickHouse's format does not and means UTC. */
function parseUtc(value: string): number {
    return Date.parse(/[Zz]|[+-]\d{2}:?\d{2}$/.test(value) ? value : `${value}Z`);
}

/** Absent cost never warns: a dead sync and a zero-spend day look identical here. */
export function costLagState(f: DataFreshness | undefined): FreshnessState {
    const stale = (f?.sources ?? []).filter((s) => s.behind_minutes !== null);
    if (!stale.length) return 'unknown';
    const worst = Math.max(...stale.map((s) => s.behind_minutes!));
    return worst * 60_000 > COST_LAG_TOLERANCE_MS ? 'late' : 'ok';
}

export function worstSource(f: DataFreshness | undefined): SourceFreshness | null {
    const list = f?.sources ?? [];
    if (!list.length) return null;
    return list.reduce((a, b) => {
        if (a.behind_minutes === null) return a;
        if (b.behind_minutes === null) return b;
        return b.behind_minutes > a.behind_minutes ? b : a;
    });
}

export function ageMs(iso: string | null, now = Date.now()): number | null {
    if (!iso) return null;
    const t = parseUtc(iso);
    return Number.isNaN(t) ? null : now - t;
}

export function shortAge(ms: number | null): string {
    if (ms === null) return '—';
    if (ms < 60_000) return 'just now';
    const mins = Math.floor(ms / 60_000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ${mins % 60}m ago`;
    return `${Math.floor(hrs / 24)}d ${hrs % 24}h ago`;
}
